import i18n from '../i18n';
import { fetchApi, fetchApiEnvelope } from './client';
import { throwIfAuthFailure } from './authError';
import type { MeetingFlag } from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description?: string;
  isChecked: boolean;
  checkedAt?: string;
  notes?: string;
  order: number;
  isCustom?: boolean; // True if this is a client-specific custom item
}

export interface ReviewDocument {
  id: string;
  fileName: string;
  fileType: string;
  documentType: string; // profit-loss, balance-sheet, trial-balance ("ai-review" only on legacy stored docs)
  blobUrl: string;
  blobName?: string; // Blob path for deletion
  uploadedAt: string;
  uploadedBy?: string;
  periodLabel?: string;
}

export interface ReviewEmail {
  id: string;
  sentAt: string;
  /** The statement period this email stated (spec §10.13.4). Absent before 2026-10-26; not backfilled. */
  statementPeriod?: StatementPeriod | null;
  /**
   * Who sent it. The literal string `'Supervisor'` on all 278 pre-2026-07-26 records — that was a
   * constant, not an attribution — and a real caller identity on every send after it. Null when the
   * caller could not be identified: an absent actor, never a guess. Nothing renders this today.
   */
  sentBy?: string | null;
  /** Stable Entra object id of the sender. Null on every pre-2026-07-26 record. */
  sentByOid?: string | null;
  recipientEmail: string;
  recipientName?: string;
  subject: string;
  templateUsed?: string;
  attachmentIds: string[];
}

export interface ClientReview {
  id: string;
  bookkeepingId: string;
  clientId: string;
  clientName: string;
  bookkeeperId: string;
  bookkeeperName: string;
  supervisorId: string;
  supervisorName: string;
  periodStart: string;
  periodEnd: string;
  xeroLink?: string;
  avgMonthlyTransactions?: number;
  complexity?: string;
  reviewType: 'quarterly' | 'year-end';
  checklistItems: ChecklistItem[];
  financialDocuments: ReviewDocument[];
  // Legacy: the AI Review surface was removed (2026-07); the backend still returns this
  // array so existing Cosmos documents keep deserializing. No UI reads it anymore.
  aiReviewDocuments: ReviewDocument[];
  emailsSent: ReviewEmail[];
  actionItemIds: string[];
  supervisorNotes?: string;
  findings?: string;
  /**
   * The STORED status, unchanged. The scheduled/ready distinction is layered on top of this
   * (see `lifecycleStatus`) rather than renaming the vocabulary under the 654 existing documents.
   */
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  /**
   * Derived server-side from (periodEnd, lead-in, now) — never stored, so it is always current.
   * `pending` resolves to `scheduled` (created and visible, but not yet actionable) until the
   * configurable lead-in before period end, then `ready`. Optional so the UI still renders if the
   * API predates the field.
   */
  lifecycleStatus?: 'scheduled' | 'ready' | 'waiting-on-bookkeeping' | 'in-progress' | 'completed' | 'cancelled' | 'unknown';
  /**
   * DP-READY-3 (2026-10-26): when `lifecycleStatus` is `waiting-on-bookkeeping`, the months of this ENDED period
   * whose bookkeeping task is still open (`YYYY-MM`). Derived per response; absent otherwise.
   */
  waitingOnMonths?: string[] | null;
  /** ISO date this review becomes actionable (period end minus the lead-in). */
  readyOn?: string;
  /** False only while `scheduled`. */
  isActionable?: boolean;
  /**
   * Who created this review — a person for the manual path, `system:review-autogen` for generated
   * ones. Null on every pre-2026-07-25 document: existing records were deliberately NOT backfilled,
   * because an inferred author is worse than an absent one. Distinct from `supervisorName`, the
   * assignee.
   */
  createdBy?: string | null;
  createdByOid?: string | null;
  createdBySource?: 'manual' | 'autogen' | null;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  /** "Bring to the next meeting" (meetings-agenda spec section 5). */
  meetingFlag?: MeetingFlag | null;
  nextTaxAppointment?: string;
  /** @deprecated A creation-day COPY on older documents. Never read it: use `clientLanguage`. */
  clientPreferredLanguage?: string;
  /** @deprecated See `clientPreferredLanguage`. */
  clientPreferredLcid?: number;
  /** The client's language, en/es/pt, read live from the CRM record via core by the server; null = unknown. */
  clientLanguage?: ClientLanguage | null;
  /** Why it was cancelled — a member of the server's close-reason vocabulary. Null when not cancelled. */
  closeReason?: string | null;
  /** Cancellation audit (POST /cancel). Null on reviews cancelled before 2026-10-20 — never inferred. */
  cancelledAt?: string | null;
  cancelledByName?: string | null;
  cancelNote?: string | null;
  mergedIntoReviewId?: string | null;
  /** Append-only period edits. Empty means "no edit recorded", not "never edited". */
  periodHistory?: ReviewPeriodChange[];
  /**
   * Derived, response-only: which of this review's months another COMPLETED review already covers.
   * Null/absent means not computed (or the review is not open) — treat as unknown, never as "none".
   */
  coverage?: ReviewCoverage | null;
  /**
   * Adjacent open reviews of the same client this one could be merged with (coverage spec §10.7),
   * each with the server's verdict. Derived per response, only on the detail read.
   */
  mergeOptions?: ReviewMergeOption[] | null;
  /** Twelve months of the review's year for this client (reviewer proposal #2). Detail read only. */
  monthStrip?: ReviewMonthStrip | null;
  /**
   * Whether the caller may hard-delete this review (coverage spec §10.10). Absent for a non-admin,
   * so the action is not offered; `{ allowed: false, reason }` says why for an admin. Detail read only.
   */
  deleteOption?: ReviewActionOption | null;
  /** Whether the caller may reinstate this cancelled review (§10.11). Same shape and rule as `deleteOption`. */
  reinstateOption?: ReviewActionOption | null;
}

/** The server's verdict on one admin action. `reason` is the sentence its refusal would carry. */
export interface ReviewActionOption {
  allowed: boolean;
  reason?: string | null;
}

export interface ReviewPeriodChange {
  from: { start: string; end: string };
  to: { start: string; end: string };
  changedBy?: string | null;
  changedByName?: string | null;
  changedAt: string;
  reason?: string | null;
  source: 'edit' | 'autogen-extend' | 'merge' | 'absorbed-by-review' | string;
  workRecorded: boolean;
  /** Completed months the new period overlapped, as they stood at the edit (spec §10.13.1). */
  overlapsCompleted?: CompletedOverlap[] | null;
  /** The open reviews this edit moved (§10.13.2). */
  adjusted?: ReviewAdjustment[] | null;
  /** On an `absorbed-by-review` row: the review whose edit took this review's months. */
  relatedReviewId?: string | null;
}

/** One completed review's months inside an edited period: who completed it and when (spec §10.13.1). */
export interface CompletedOverlap {
  reviewId: string;
  /** `YYYY-MM`, ascending; each month names one review, the earliest completion. */
  months: string[];
  completedByName?: string | null;
  /** The assignee, shown only when the completer was not recorded. */
  reviewerName?: string | null;
  completedAt?: string | null;
}

/** What a period edit does to one other open review (spec §10.13.2). */
export interface ReviewAdjustment {
  reviewId: string;
  outcome: 'shrunk' | 'cancelled';
  from: { start: string; end: string };
  /** The period it keeps; null when it is cancelled (absorbed-by-review). */
  to?: { start: string; end: string } | null;
  monthsTaken: string[];
  /** Months it gives up without this review taking them (an edit into its interior). */
  monthsReleased: string[];
  workRecorded: boolean;
}

/** GET client-reviews/{id}/period-preview: exactly what saving that period would do. */
export interface PeriodEditPlan {
  allowed: boolean;
  problem?: string | null;
  error?: string | null;
  periodStart: string;
  periodEnd: string;
  overlapsCompleted: CompletedOverlap[];
  adjustments: ReviewAdjustment[];
  /** What this review adds: its new months no completed review already covers. */
  adds: { start: string; end: string }[];
}

/** PATCH client-reviews/{id}/period answers the review AND what it did to its neighbours. */
export interface PeriodEditResult {
  success: boolean;
  error?: string;
  data: ClientReview;
  adjusted: ReviewAdjustment[];
  /** Neighbours that could not be moved: both reviews now claim the month (visible, harmless). */
  adjustmentsFailed: string[];
}

export interface ReviewMonthStrip {
  year: number;
  months: {
    month: string;
    state: 'reviewed' | 'in-review' | 'not-started' | 'none';
    reviewIds: string[];
    inThisReview: boolean;
    /** On a reviewed month: who completed the review credited with it (the earliest completion). */
    reviewedByName?: string | null;
    reviewedOn?: string | null;
  }[];
}

/** One month's readiness (docs/specs/month-readiness.md), and the Bookkeeping task that decided it. */
export interface MonthReadinessRow {
  month: string;
  state: 'ready' | 'not-ended' | 'bookkeeping-open' | 'no-task';
  taskSubject?: string | null;
  due?: string | null;
  completedAt?: string | null;
}

/** `available: false` means this backend cannot say, which is not the same as "nothing is ready". */
export interface MonthReadinessResponse {
  available: boolean;
  year: number;
  months: MonthReadinessRow[];
}

export interface ReviewMergeOption {
  otherReviewId: string;
  otherPeriodStart: string;
  otherPeriodEnd: string;
  otherStatus: string;
  allowed: boolean;
  /** Why not, when not allowed — the same sentence the merge endpoint's 409 carries. */
  reason: string | null;
  survivorId: string | null;
  mergedStart: string | null;
  mergedEnd: string | null;
}

export interface ReviewCoverage {
  kind: 'none' | 'full' | 'partial-end' | 'partial-split';
  /** `YYYY-MM`, ascending. */
  coveredMonths: string[];
  coveringReviews: {
    id: string;
    periodStart: string;
    periodEnd: string;
    completedByName?: string | null;
    reviewerName?: string | null;
    completedAt?: string | null;
  }[];
  /** Still-unreviewed runs of this review's period, `YYYY-MM` inclusive. */
  remainder: { start: string; end: string }[];
}

/** The close reasons a person may pick, with the label shown for each. Mirrors ReviewCloseReasonVocabulary. */
export const CANCEL_REASONS: { value: string; label: string; needsNote?: boolean }[] = [
  { value: 'covered-by-another-review', label: 'Already reviewed in another review' },
  { value: 'duplicate', label: 'Duplicate — minted twice for the same months' },
  { value: 'created-in-error', label: 'Created in error' },
  { value: 'engagement-never-commenced', label: 'Engagement never started' },
  { value: 'record-deactivated', label: 'Bookkeeping record deactivated (no close-out owed)' },
  { value: 'superseded-by-closing-review', label: 'Replaced by the closing review' },
  { value: 'other', label: 'Other (note required)', needsNote: true },
];

/**
 * Close reasons only the SYSTEM writes, never offered in the cancel picker: a merge (§10.7) and a period edit
 * that took every month of another review (§10.13.2). They still need words when a cancelled review shows them.
 */
export const SYSTEM_CANCEL_REASONS = ['merged-into-review', 'absorbed-by-review'] as const;

/** A cancel reason in the UI language; an unknown stored value is shown as stored. */
export function cancelReasonLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const known = CANCEL_REASONS.some((r) => r.value === value) || (SYSTEM_CANCEL_REASONS as readonly string[]).includes(value);
  return known ? i18n.t(`cancelReason.${value}`) : value;
}

/** A bulk action on the Reviews grid (docs/specs/reviews-bulk-actions.md). */
export type BulkReviewAction = 'start' | 'complete' | 'cancel' | 'reassign-reviewer';

export interface BulkReviewRequest {
  action: BulkReviewAction;
  ids: string[];
  dryRun?: boolean;
  closeReason?: string;
  note?: string;
  supervisorId?: string;
  supervisorName?: string;
  /** Continuation: batches after the first carry the first batch's id, so one action keeps one id. */
  bulkId?: string;
}

export interface BulkRowOutcome {
  id: string;
  clientName: string | null;
  outcome: 'done' | 'skipped' | 'failed';
  reason: string | null;
}

export interface BulkReviewResult {
  /** Null on a dry run. Ties every changed row's audit entry together. */
  bulkId: string | null;
  dryRun: boolean;
  action: BulkReviewAction;
  results: BulkRowOutcome[];
  summary: { done: number; skipped: number; failed: number };
}

export interface CreateReviewRequest {
  bookkeepingId: string;
  clientId: string;
  clientName: string;
  bookkeeperId: string;
  bookkeeperName: string;
  supervisorId: string;
  supervisorName: string;
  periodStart: string;
  periodEnd: string;
  xeroLink?: string;
  avgMonthlyTransactions?: number;
  reviewType?: 'quarterly' | 'year-end';
  nextTaxAppointment?: string;
  clientPreferredLanguage?: string;
  clientPreferredLcid?: number;
  /** Sent by the Suggested tab only (graded-automation spec §1). */
  fromSuggestion?: SuggestionSnapshot;
}

/** The Suggested queue as the reviewer saw it at Create review: `rank` is the engine's order, 1-based. */
export interface SuggestionSnapshot {
  rank: number;
  of: number;
  priorityScore?: number;
  computedAt?: string;
  topIds: string[];
  engineBuild?: string;
}

export interface UpdateChecklistRequest {
  itemId: string;
  isChecked: boolean;
  notes?: string;
}

/**
 * The months a client's statements cover (spec §10.13.4), NOT the review period. Unmarked → the server uses
 * year-to-date of the review's year and ignores any months passed; marked custom → those months, any years.
 */
export interface StatementPeriodRequest {
  statementCustom?: boolean;
  /** `YYYY-MM`; read only when `statementCustom` is true. */
  statementStart?: string;
  statementEnd?: string;
}

/** As recorded on a sent email; absent on emails sent before 2026-10-26. */
export interface StatementPeriod {
  start: string;
  end: string;
  custom: boolean;
}

export interface SendEmailRequest extends StatementPeriodRequest {
  recipientEmail: string;
  recipientName?: string;
  subject?: string;
  body?: string;
  senderEmail?: string;
  senderName?: string;
  targetLanguage?: string;
  updateDataverse?: boolean;
}

export interface PreviewEmailRequest extends StatementPeriodRequest {
  subject?: string;
  body?: string;
  targetLanguage?: string;
  updateDataverse?: boolean;
}

export interface EmailPreviewResponse {
  reviewId: string;
  clientName: string;
  clientEmail?: string;
  subject: string;
  body: string;
  originalBody?: string;
  targetLanguage: string;
  targetLanguageName?: string;
  wasTranslated: boolean;
  attachmentCount: number;
  attachmentNames: string[];
  /** The statement period the draft states, as the server resolved it. */
  statementPeriod?: StatementPeriod | null;
  statementPeriodLabel?: string | null;
}

export interface SuggestedReview {
  bookkeepingId: string;
  clientId: string;
  clientName: string;
  bookkeeperId: string;
  bookkeeperName: string;
  xeroLink?: string;
  avgMonthlyTransactions?: number;
  outstandingTasks: number;
  completedTasks: number;
  qtrReviewDone: boolean;
  transactionLockDate?: string;
  priority?: number;
  priorityScore: number;
  priorityReason: string;
  nextTaxAppointment?: string;
  upcomingAppointments: AppointmentSummary[];
  // Last review info for task count period
  lastReviewEndDate?: string;
  lastReviewType?: string;
  taskCountPeriod?: string;
  /** First day of the task window, `yyyy-MM-dd`; the UI words it (src/utils/taskWindow). */
  taskCountSince?: string | null;
  hasActiveReview?: boolean;
}

export interface AppointmentSummary {
  subject: string;
  scheduledDate: string;
  isTaxRelated: boolean;
}

export const reviewsApi = {
  getReviews: async (params?: {
    supervisorId?: string;
    bookkeeperId?: string;
    status?: string;
  }): Promise<ClientReview[]> => {
    const searchParams = new URLSearchParams();
    if (params?.supervisorId) searchParams.set('supervisorId', params.supervisorId);
    if (params?.bookkeeperId) searchParams.set('bookkeeperId', params.bookkeeperId);
    if (params?.status) searchParams.set('status', params.status);

    const queryString = searchParams.toString();
    return fetchApi<ClientReview[]>(`/client-reviews${queryString ? `?${queryString}` : ''}`);
  },

  getReview: async (id: string): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}`);
  },

  getMonthReadiness: async (id: string): Promise<MonthReadinessResponse> => {
    return fetchApi<MonthReadinessResponse>(`/client-reviews/${id}/month-readiness`);
  },

  /**
   * Tells the API that the assigned reviewer has this review OPEN — the signal the server uses to
   * stamp `startedAt` on genuine first interaction.
   *
   * MUST stay a deliberate POST issued only from a mounted review detail page. It is intentionally
   * NOT folded into `getReview`, because `prefetchClientReview` calls `getReview` on grid row hover:
   * merging them would make "started reviewing" mean "moved the mouse over a row", which is the
   * exact class of meaningless data this signal exists to replace. Never call this from a list,
   * prefetch, background refresh or hover handler.
   *
   * Idempotent server-side — the first touch wins and later calls change nothing.
   */
  markReviewOpened: async (id: string): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/open`, {
      method: 'POST',
    });
  },

  createReview: async (request: CreateReviewRequest): Promise<ClientReview> => {
    return fetchApi<ClientReview>('/client-reviews', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  updateReview: async (id: string, data: Partial<ClientReview>): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  updateChecklist: async (id: string, request: UpdateChecklistRequest): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/checklist`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  },

  startReview: async (id: string): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/start`, {
      method: 'POST',
    });
  },

  completeReview: async (id: string): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/complete`, {
      method: 'POST',
    });
  },

  /** PATCH /client-reviews/{id}/period — whole months, `YYYY-MM-DD`; refused (409) on overlap. */
  updatePeriod: async (
    id: string,
    body: { periodStart: string; periodEnd: string; reason?: string }
  ): Promise<PeriodEditResult> => {
    return fetchApiEnvelope<PeriodEditResult>(`/client-reviews/${id}/period`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  previewPeriod: async (id: string, periodStart: string, periodEnd: string): Promise<PeriodEditPlan> => {
    const q = new URLSearchParams({ periodStart, periodEnd });
    return fetchApi<PeriodEditPlan>(`/client-reviews/${id}/period-preview?${q.toString()}`);
  },

  /** POST /client-reviews/{id}/cancel — a reason is required; `other` requires a note. */
  cancelReview: async (id: string, body: { closeReason: string; note?: string }): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /client-reviews/{id}/reinstate — admin only, a reason required; refused (409) when its months are taken. */
  reinstateReview: async (id: string, body: { reason: string }): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${id}/reinstate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /client-reviews/{id}/merge — spec §10.7. Supersede, never delete. */
  mergeReview: async (
    id: string,
    body: { withReviewId: string; reason?: string }
  ): Promise<{ survivor: ClientReview; absorbed: ClientReview }> => {
    return fetchApi<{ survivor: ClientReview; absorbed: ClientReview }>(`/client-reviews/${id}/merge`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /client-reviews-bulk — every id comes back once with its outcome. Max 200 ids. */
  bulk: async (body: BulkReviewRequest): Promise<BulkReviewResult> => {
    return fetchApi<BulkReviewResult>('/client-reviews-bulk', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** DELETE /client-reviews/{id} — admin only, and only a review with no recorded work (spec §10.10). */
  deleteReview: async (id: string): Promise<void> => {
    await fetchApi<void>(`/client-reviews/${id}`, {
      method: 'DELETE',
    });
  },

  getAvailableClients: async () => {
    // NOT '/client-reviews/available-clients': that path has the same method and depth as
    // GET /client-reviews/{id}, and the Functions host resolves routes by registration order rather
    // than specificity, so the literal is one function rename away from being swallowed. Backend
    // route renamed to the unshadowable single-segment literal (see ClientReviewFunctions and
    // api/Tests/ShadowedRouteTests.cs); same fix as '/meetings-upcoming'.
    return fetchApi<unknown[]>('/client-reviews-available-clients');
  },

  getSuggestedReviews: async (): Promise<SuggestedReview[]> => {
    return fetchApi<SuggestedReview[]>('/suggested-reviews');
  },

  sendEmail: async (reviewId: string, request: SendEmailRequest): Promise<ClientReview> => {
    return fetchApi<ClientReview>(`/client-reviews/${reviewId}/send-email`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  previewEmail: async (reviewId: string, request?: PreviewEmailRequest): Promise<EmailPreviewResponse> => {
    return fetchApi<EmailPreviewResponse>(`/client-reviews/${reviewId}/preview-email`, {
      method: 'POST',
      body: JSON.stringify(request || {}),
    });
  },

  uploadDocument: async (
    reviewId: string,
    file: File,
    documentType: string,
    uploadedBy?: string
  ): Promise<ReviewDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (uploadedBy) {
      formData.append('uploadedBy', uploadedBy);
    }

    const response = await fetch(`${API_BASE}/client-reviews/${reviewId}/documents`, {
      method: 'POST',
      body: formData,
    });

    throwIfAuthFailure(response);

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API error: ${response.status} - ${error}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to upload document');
    }

    return result.data;
  },

  deleteDocument: async (reviewId: string, documentId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/client-reviews/${reviewId}/documents/${documentId}`, {
      method: 'DELETE',
    });

    throwIfAuthFailure(response);

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API error: ${response.status} - ${error}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete document');
    }
  },

  // Email Templates
  getEmailTemplates: async (project?: string, category?: string): Promise<EmailTemplate[]> => {
    const params = new URLSearchParams();
    if (project) params.append('project', project);
    if (category) params.append('category', category);
    const queryString = params.toString();
    return fetchApi<EmailTemplate[]>(`/email-templates${queryString ? `?${queryString}` : ''}`);
  },

  renderEmailTemplate: async (request: RenderTemplateRequest): Promise<RenderedTemplate> => {
    return fetchApi<RenderedTemplate>('/email-templates/render', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Client Language
  updateClientLanguage: async (clientId: string, lcid: number): Promise<UpdateLanguageResponse> => {
    return fetchApi<UpdateLanguageResponse>(`/clients/${clientId}/language`, {
      method: 'PUT',
      body: JSON.stringify({ lcid }),
    });
  },

  getClientLanguage: async (clientId: string): Promise<ClientLanguageInfo> => {
    return fetchApi<ClientLanguageInfo>(`/clients/${clientId}/language`);
  },
};

// Email Template Types
export interface EmailTemplateContent {
  subject: string;
  body: string;
  htmlBody?: string;
}

export interface TemplatePlaceholder {
  key: string;
  description?: string;
  required: boolean;
  defaultValue?: string;
  example?: string;
}

export interface EmailTemplate {
  id: string;
  project: string;
  templateKey: string;
  name: string;
  description?: string;
  category: string;
  defaultLanguage: string;
  content: Record<string, EmailTemplateContent>;
  placeholders: TemplatePlaceholder[];
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface RenderTemplateRequest {
  templateKey: string;
  project?: string;
  language?: string;
  placeholders: Record<string, string>;
}

export interface RenderedTemplate {
  templateId: string;
  templateKey: string;
  language: string;
  subject: string;
  body: string;
  htmlBody?: string;
}

export interface UpdateLanguageResponse {
  clientId: string;
  lcid: number;
  languageName?: string;
}

export interface ClientLanguageInfo {
  clientId: string;
  clientName?: string;
  primaryEmail?: string;
  preferredLcid?: number;
}

/**
 * The CLIENT languages (fleet platform ruling, Christian, 2026-10-27): exactly English, Spanish and Portuguese, read
 * from the client's CRM record via core; blank = unknown. The staff UI stays en/es; this list is for what a CLIENT
 * reads. Names are UI text, so they are getters read in the current UI language.
 */
export const CLIENT_LANGUAGES = ['en', 'es', 'pt'] as const;
export type ClientLanguage = (typeof CLIENT_LANGUAGES)[number];
export const LANGUAGE_OPTIONS = CLIENT_LANGUAGES.map((code) => ({
  code,
  get name() { return i18n.t(`emailTemplates.languages.${code}`); },
}));

export interface StaffLanguageInfo {
  systemUserId: string;
  fullName?: string;
  preferredLanguageCode: string;
  preferredLanguageName: string;
  /** True only when the CRM really holds a language; `preferredLanguageCode` is "en" for nobody-recorded too. */
  recorded?: boolean;
}

// Staff Language API
export const staffApi = {
  getStaffLanguage: async (userId: string): Promise<StaffLanguageInfo> => {
    return fetchApi<StaffLanguageInfo>(`/staff/${userId}/language`);
  },
  getStaffLanguageByAadId: async (aadObjectId: string): Promise<StaffLanguageInfo> => {
    return fetchApi<StaffLanguageInfo>(`/staff/aad/${aadObjectId}/language`);
  },
  updateStaffLanguageByAadId: async (aadObjectId: string, languageCode: string): Promise<StaffLanguageInfo> => {
    return fetchApi<StaffLanguageInfo>(`/staff/aad/${aadObjectId}/language`, {
      method: 'PUT',
      body: JSON.stringify({ languageCode }),
    });
  },
};
